import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter, Loader2 } from "lucide-react";
import { CATEGORIES, WA_LINK } from "../lib/constants";
import { fetchPublic } from "../lib/content";
import { CourseCard } from "../components/CourseCard";
import { PageHero } from "../components/PageHero";
import { Button } from "../components/ui/button";
import { MessageCircle } from "lucide-react";

export default function Courses() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("cat") || "all";
  const [active, setActive] = useState(initialCat);
  const [query, setQuery] = useState("");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublic("courses")
      .then((d) => setCourses(d))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setActive(params.get("cat") || "all");
  }, [params]);

  const filtered = useMemo(() => {
    let list = courses;
    if (active !== "all") list = list.filter((c) => c.category === active);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q));
    }
    return list;
  }, [active, query, courses]);

  const handleSetCat = (id) => {
    setActive(id);
    if (id === "all") setParams({});
    else setParams({ cat: id });
  };

  return (
    <div data-testid="courses-page">
      <PageHero
        eyebrow="All Courses"
        title="Find the program that fits your career goal."
        desc="20+ AI-powered, 20+ Computer Education, 15+ Short-term, 8+ Professional programs and 18+ University degrees — pick by category or search."
        image="https://images.pexels.com/photos/4144222/pexels-photo-4144222.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920"
        alt="Students learning in classroom"
      />

      {/* Search + filters */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search e.g. Tally, AI, Web, BBA..."
                data-testid="course-search-input"
                className="w-full pl-11 pr-4 h-12 rounded-full border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#00B37E] focus:ring-2 focus:ring-[#00B37E]/20"
              />
            </div>
            <a
              href={WA_LINK("Hi TASSEI, I want a free consultation")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="courses-consult-cta"
            >
              <Button className="bg-[#00B37E] hover:bg-[#009166] text-white font-semibold rounded-full h-12 px-6">
                <MessageCircle className="w-4 h-4 mr-2" /> Not sure? Ask Us
              </Button>
            </a>
          </div>

          <div className="mt-5 flex gap-2 flex-wrap" data-testid="category-filter-bar">
            <button
              onClick={() => handleSetCat("all")}
              data-testid="filter-all"
              className={`px-4 h-10 rounded-full text-sm font-semibold transition-all ${
                active === "all"
                  ? "bg-[#0A192F] text-white"
                  : "bg-white border border-slate-200 text-[#0A192F] hover:border-[#00B37E]"
              }`}
            >
              <Filter className="w-3.5 h-3.5 inline mr-1.5" /> All Courses
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => handleSetCat(c.id)}
                data-testid={`filter-${c.id}`}
                className={`px-4 h-10 rounded-full text-sm font-semibold transition-all ${
                  active === c.id
                    ? "bg-[#00B37E] text-white"
                    : "bg-white border border-slate-200 text-[#0A192F] hover:border-[#00B37E]"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* List */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          {loading ? (
            <div className="flex items-center gap-2 text-[#64748B] py-20 justify-center" data-testid="courses-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading courses…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-[#64748B]" data-testid="no-courses-found">
              No courses matched. Try a different search.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="courses-grid">
              {filtered.map((c) => {
                const cat = CATEGORIES.find((x) => x.id === c.category);
                return <CourseCard key={c.id} course={c} categoryName={cat?.name || ""} />;
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
